import { useCallback, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Input, Select, Tooltip, Image } from "antd";

const { TextArea } = Input;
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconArrowDown, IconHelp } from "@icons/index";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { CommonButton } from "@components/CommonButton";
import type { ProductImage } from "../videoCreatingSlice";
import {
  setProductInfo,
  setSellingPoints,
  selectVideoCreatingParams,
  selectStepStatus,
  setStepStatus,
} from "../videoCreatingSlice";
import SellingPoints from "@pages/aiStudio/components/SellingPoints";
import useSellingPoints from "@pages/aiStudio/components/SellingPoints/useSellingPoints";
import { VideoParamsSelect } from "../VideoParamsSelect";
import TruncateTitle from "@components/TruncateTitle";
import report from "@report";
import { useCreateVideoOptions } from "@hooks/useCreateVideoOptions";
import { useMount } from "ahooks";
import { ProductInfoByLinkResponseLinkSource } from "@apis/generated/openapi";
import { SelectLanguage } from "@components/SelectLanguage/SelectLanguage";
import { truncateUnicodeText } from "@kalodata/tools";

const CREATION_DESCRIPTION_MAX_LENGTH = 1000;

export interface ProductInfoRef {
  triggerNext: () => void;
}

interface ProductInfoProps {
  onNext?: () => void;
}

export const ProductInfo = forwardRef<ProductInfoRef, ProductInfoProps>(({ onNext }, ref) => {
  const dispatch = useAppDispatch();
  const { sellingCountry: sellingCountryOptions } = useAppSelector((state) => state.configReducer);
  const params = useAppSelector(selectVideoCreatingParams);
  const { basicInfo, productInfo, productId, country } = params;
  const { productTitle, images, linkSource, productCategory } = basicInfo;
  const [sellingCountry, setSellingCountry] = useState<string | undefined>(productInfo.sellingCountry || undefined);
  const [language, setLanguage] = useState(productInfo.language);
  const [creationDescription, setCreationDescription] = useState(productInfo.creationDescription ?? "");
  const [sellingPointsData, setSellingPointsData] = useState(productInfo.sellingPoints);
  const { sellingPointsData: fetchedSellingPoints, fetchSellingPoints, inferSellingPoints } = useSellingPoints();
  const stepStatus = useAppSelector(selectStepStatus);
  const [popoverWidth, setPopoverWidth] = useState<number>(0);
  const panelRef = useRef<HTMLDivElement>(null);
  useCreateVideoOptions(language);

  // 监听按钮宽度变化
  useEffect(() => {
    if (!panelRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPopoverWidth(entry.contentRect.width - 48);
      }
    });
    observer.observe(panelRef.current);
    // 初始化宽度
    setPopoverWidth(panelRef.current.offsetWidth - 48);
    return () => observer.disconnect();
  }, []);

  const handleNext = useCallback(() => {
    dispatch(
      setProductInfo({
        sellingCountry: sellingCountry || "",
        language,
        creationDescription:
          truncateUnicodeText(creationDescription.trim(), CREATION_DESCRIPTION_MAX_LENGTH) || undefined,
      }),
    );
    dispatch(setSellingPoints(sellingPointsData));
    report.click({ kalo_type: "click_next_step2" });
    onNext?.();
  }, [sellingCountry, language, creationDescription, sellingPointsData, dispatch, onNext]);

  useImperativeHandle(ref, () => ({ triggerNext: handleNext }), [handleNext]);

  const handleSellingPointsChange = useCallback((data: any) => {
    if (Array.isArray(data)) {
      setSellingPointsData({ data, status: "succeeded", error: null });
    }
  }, []);

  useMount(() => {
    if (!stepStatus.productInfoInit) {
      const imageUrl = images?.[0]?.url || "";
      if (productId === "custom") {
        if (imageUrl && productTitle) {
          inferSellingPoints({ imageUrl, productTitle, productCategory });
        } else {
          setSellingPointsData({ data: [], status: "succeeded", error: null });
        }
      } else if (productId && country) {
        const isFromAmazon = linkSource === ProductInfoByLinkResponseLinkSource.AMAZON;
        if (isFromAmazon) {
          inferSellingPoints({ imageUrl, productTitle, productCategory });
        } else {
          fetchSellingPoints({ id: productId, country });
        }
      } else {
        setSellingPointsData({ data: [], status: "succeeded", error: null });
      }
      dispatch(setStepStatus({ productInfoInit: true } as any));
    }
  });

  // 接口返回后同步到本地 state 和 Redux
  useEffect(() => {
    if (fetchedSellingPoints.status === "succeeded" || fetchedSellingPoints.status === "failed") {
      const normalizedData = {
        ...fetchedSellingPoints,
        data: (fetchedSellingPoints.data || []).map((item: any) => ({ checked: true, ...item })),
      };
      setSellingPointsData(normalizedData);
      dispatch(setSellingPoints(normalizedData));
    } else if (fetchedSellingPoints.status === "loading") {
      setSellingPointsData((prev: any) => ({ ...prev, status: "loading" }));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [fetchedSellingPoints.status]);

  // Redux 异步到达时同步到本地 state（fetchJobDetail / fetchProductDetail 可能在 Step 2 挂载后才返回）
  const {
    sellingCountry: reduxSellingCountry,
    language: reduxLanguage,
    creationDescription: reduxCreationDescription,
  } = productInfo;

  useEffect(() => {
    if (reduxSellingCountry && !sellingCountry) setSellingCountry(reduxSellingCountry);
    if (reduxLanguage && !language) setLanguage(reduxLanguage);
    if (reduxCreationDescription && !creationDescription) setCreationDescription(reduxCreationDescription);
  }, [reduxSellingCountry, reduxLanguage, reduxCreationDescription]);

  return (
    <div className="ProductInfo flex flex-col h-[calc(100vh-125px)] bg-kds-bg relative" ref={panelRef}>
      <div className="flex-1 overflow-y-auto p-[24px] pr-[16px] border-x border-kds-border">
        {/* Product Name & Images Preview */}
        <section className="mb-[16px]">
          <div className="text-kds-fg font-bold text-[16px] mb-[8px]">
            <TruncateTitle text={productTitle || "Product"} placement="top" />
          </div>
          {images.length > 0 && (
            <div className="flex gap-[8px] flex-wrap pb-[16px] border-b border-kds-border">
              {images.map((img: ProductImage, index: number) => (
                <div
                  key={img.id || index}
                  className="bg-kds-bg-element border border-kds-border w-[54px] h-[54px] rounded-[4px] overflow-hidden"
                >
                  <Image alt="basic" src={img.url} className="object-contain w-full h-full" />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Selling Points */}
        <section className="mb-[16px]">
          <div className="flex items-center gap-1 mb-[8px]">
            <span className="text-kds-fg font-bold text-[16px]">
              <Trans>Selling Points</Trans>
            </span>
            <Tooltip title={t("Select the key selling points to highlight in your video.")}>
              <span>
                <IconHelp className="w-[16px] h-[16px] text-kds-fg-subtle cursor-pointer" />
              </span>
            </Tooltip>
          </div>
          <SellingPoints
            popoverWidth={popoverWidth}
            externalData={sellingPointsData}
            onChange={handleSellingPointsChange}
          />
        </section>

        {/* Target Market & Language */}
        <section className="flex gap-4 mb-[16px]">
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-[8px]">
              <span className="text-kds-fg font-bold text-[16px]">
                <Trans>Target Market</Trans>
              </span>
              {/* <Tooltip title={t("Select the region you are selling to")}>
                <span>
                  <IconHelp className="w-[16px] h-[16px] text-kds-fg-subtle cursor-pointer" />
                </span>
              </Tooltip> */}
            </div>
            <Select
              value={sellingCountry}
              onChange={setSellingCountry}
              placeholder={t("Select the region you are selling to...")}
              className="w-full h-[44px]"
              options={sellingCountryOptions.map((r) => ({ label: t(r.label), value: r.name }))}
              suffixIcon={<IconArrowDown className="w-[16px] h-[16px] text-kds-fg-subtle" />}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-[8px]">
              <span className="text-kds-fg font-bold text-[16px]">
                <Trans>Language</Trans>
              </span>
              <Tooltip title={t("Language for voiceovers and model speech.")}>
                <span>
                  <IconHelp className="w-[16px] h-[16px] text-kds-fg-subtle cursor-pointer" />
                </span>
              </Tooltip>
            </div>
            <SelectLanguage value={language} onChange={setLanguage} className="h-[44px]" />
          </div>
        </section>

        {/* Custom Instructions */}
        <section className="mb-[16px]">
          <div className="text-kds-fg font-bold text-[16px] mb-[8px]">
            <Trans>Custom Instructions</Trans>
          </div>
          <TextArea
            value={creationDescription}
            onChange={(e) => setCreationDescription(e.target.value)}
            placeholder={t("Any special requests? (e.g., specific scenes, style, or things to avoid)")}
            maxLength={CREATION_DESCRIPTION_MAX_LENGTH}
            showCount
            rows={4}
            className="bg-kds-bg-element border-none text-kds-fg"
            style={{ resize: "none" }}
          />
        </section>
      </div>

      {/* Footer - 参数选项 */}
      <div className="bg-kds-bg border-t border-kds-border p-[16px] flex items-center justify-between gap-[8px]">
        <VideoParamsSelect showResolution />
        <div className="flex-1" />
        <CommonButton size="large" type="primary" className="min-w-[96px]" onClick={handleNext}>
          <Trans>Next</Trans>
        </CommonButton>
      </div>
    </div>
  );
});

ProductInfo.displayName = "ProductInfo";
